import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Card, Form, Select, DatePicker, Button, Space, Popconfirm, message } from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { SearchOutlined, DeleteOutlined, CheckOutlined } from "@ant-design/icons";
import * as adminactions from "store/actions/AuthActions";
import dayjs from "dayjs";
import useUrlFilters from "hooks/useUrlFilters";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Applied filters live in the URL so a filtered view can be shared as a link.
const URL_FILTERS = {
  range: { type: "dateRange", keys: ["date_from", "date_to"] },
  machineSerial: { type: "array", key: "device" },
  employees: { type: "array", key: "employee" },
  page: { type: "number", default: 1 },
  pageSize: { type: "number", key: "page_size", default: 10 },
};

const MachineLogs = () => {
  const [form] = Form.useForm();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [devices, setDevices] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS);
  const pageSize = Math.min(urlFilters.pageSize, MAX_PAGE_SIZE);
  const filters = useMemo(() => ({
    dateFrom: urlFilters.range ? urlFilters.range[0].format("YYYY-MM-DD") : undefined,
    dateTo: urlFilters.range ? urlFilters.range[1].format("YYYY-MM-DD") : undefined,
    machineSerial: urlFilters.machineSerial.length ? urlFilters.machineSerial : undefined,
    employees: urlFilters.employees.length ? urlFilters.employees : undefined,
  }), [urlFilters]);


  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Employee ID",
      dataIndex: "employee_id",
      key: "employee_id",
      width: 120,
    },
    {
      title: "Employee Name",
      dataIndex: ["employee", "name"],
      key: "employee_name",
      render: (text, record) => text || record.employee_name || "-",
    },
    {
      title: "Machine ID",
      dataIndex: "machine_id",
      key: "machine_id",
      width: 120,
    },
    {
      title: "Serial Number",
      dataIndex: "machine_sn",
      key: "machine_sn",
      width: 150,
    },
    {
      title: "Time Input",
      dataIndex: "time_input",
      key: "time_input",
      render: (text) => (text ? dayjs(text).format("DD MMM YYYY HH:mm:ss") : "-"),
      width: 180,
    },
    {
      title: "Type",
      dataIndex: "punch_type",
      key: "punch_type",
      width: 100,
    },
  ];

  const fetchLogs = async (page = 1, currentFilters = filters) => {
    setLoading(true);
    try {
      const response = await adminactions.loadMachineLogs(
        currentFilters,
        page,
        pageSize
      );

      if (response) {
        setLogs(response.results || response);
        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total: response.count || response.length || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching machine logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await adminactions.loadEmployees(1, undefined, 1000);
      if (response?.status === 200) {
        setEmployees(response.data.results || []);
      }
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadDevices = async () => {
    try {
      const response = await adminactions.loadDevices(1000);
      if (response) {
        setDevices(response.results || response);
      }
    } catch (error) {
      console.error("Error loading devices:", error);
    }
  };

  useEffect(() => {
    loadEmployees();
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLogs(urlFilters.page, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFilters, filters]);

  // Keep the filter form in sync with the URL (shared links, back/forward).
  // Employee ids come back from the URL as strings, so match them to the option values.
  useEffect(() => {
    form.setFieldsValue({
      dateRange: urlFilters.range || undefined,
      machineSerial: urlFilters.machineSerial.length ? urlFilters.machineSerial : undefined,
      employees: urlFilters.employees.length
        ? urlFilters.employees.map((id) => employees.find((emp) => String(emp.id) === id)?.id ?? id)
        : undefined,
    });
  }, [urlFilters, employees]);

  const handleSearch = (values) => {
    setUrlFilters({
      range: values.dateRange || null,
      machineSerial: values.machineSerial || null,
      employees: values.employees || null,
      page: 1,
    });
  };

  const handleReset = () => {
    form.resetFields();
    setUrlFilters({ range: null, machineSerial: null, employees: null, page: 1 });
  };

  const handleTableChange = (paginationConfig) => {
    // A new page size starts again from page 1
    const sizeChanged = paginationConfig.pageSize !== pageSize;
    setUrlFilters({ page: sizeChanged ? 1 : paginationConfig.current, pageSize: paginationConfig.pageSize });
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleProcessLogs = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select logs to process");
      return;
    }

    const result = await adminactions.processMachineLogs(selectedRowKeys);
    if (result) {
      message.success("Logs processed successfully");
      setSelectedRowKeys([]);
      fetchLogs(pagination.current);
    }
  };

  const handleDeleteLogs = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select logs to delete");
      return;
    }

    const result = await adminactions.deleteMachineLogs(selectedRowKeys);
    if (result) {
      setSelectedRowKeys([]);
      fetchLogs(pagination.current);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={6}>
          <Card title="Retrieve Logs" size="small">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSearch}
            >
              <Form.Item
                label="Date Range"
                name="dateRange"
              >
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Device"
                name="machineSerial"
              >
                <Select
                  mode="multiple"
                  placeholder="Select device(s)"
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={devices.map((d) => ({
                    value: d.serial_number,
                    label: d.name ? `${d.name} (${d.serial_number})` : d.serial_number,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="Employees"
                name="employees"
              >
                <Select
                  mode="multiple"
                  placeholder="Select employees"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {employees.map((emp) => (
                    <Option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_id})
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>
                  <Button onClick={handleReset}>
                    Reset
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={18}>
          <Card
            title="Machine Logs"
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleProcessLogs}
                  disabled={selectedRowKeys.length === 0}
                >
                  Process Selected
                </Button>
                <Popconfirm
                  title="Are you sure you want to delete selected logs?"
                  onConfirm={handleDeleteLogs}
                  okText="Yes"
                  cancelText="No"
                  disabled={selectedRowKeys.length === 0}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={selectedRowKeys.length === 0}
                  >
                    Delete Selected
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            {selectedRowKeys.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <span>{selectedRowKeys.length} items selected</span>
              </div>
            )}
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={logs}
              rowKey="id"
              loading={loading}
              pagination={{ ...pagination, showSizeChanger: true }}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MachineLogs;
